import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import { Link } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/context/AuthContext';
import { listSuppliers, SUPPLIER_TYPES } from '@/lib/suppliers/service';
import { SchemaNotice, Loading, StatusPill, ReadinessGate } from '@/components/common/F1States';
import { useSchemaReady } from '@/lib/schemaReady';
import { NewSupplierDialog } from '@/components/suppliers/NewSupplierDialog';
import { SupplierImportDialog } from '@/components/suppliers/SupplierImportDialog';

const typeLabel = (t) => SUPPLIER_TYPES.find(([k]) => k === t)?.[1] || t;

export default function SuppliersPage() {
  const { organisationId, role } = useAuth();
  const financial = ['owner', 'admin', 'manager'].includes(role || '');
  const isAdmin = ['owner', 'admin'].includes(role || '');
  const [filter, setFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const readiness = useSchemaReady(organisationId && 'suppliers', ['suppliers', 'v_suppliers_basic']);
  const ready = readiness.state === 'ready';
  const { data, error, isLoading, mutate } = useSWR(organisationId && ready ? ['suppliers', organisationId, financial] : null, () => listSuppliers(organisationId, { financial }));

  const rows = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return (data || []).filter((s) => !f || [s.display_name, s.entity?.legal_name, ...(s.categories || [])].some((v) => (v || '').toLowerCase().includes(f)));
  }, [data, filter]);

  if (!ready) {
    return (
      <div data-testid="suppliers-page" className="p-6 md:p-8 max-w-7xl mx-auto">
        <PageHeader title="Supplier Directory" subtitle="Register, account terms and compliance" actions={isAdmin && <Button data-testid="supplier-new-button" disabled title="Available once F-1 is installed" className="bg-bronze text-white"><Plus size={14} className="mr-1" /> New supplier</Button>} />
        <ReadinessGate readiness={readiness} module="The Supplier Directory" migration="f1_foundation_migration.sql" testId="suppliers-migration-pending" />
      </div>
    );
  }

  return (
    <div data-testid="suppliers-page" className="p-6 md:p-8 max-w-7xl mx-auto">
      <PageHeader
        title="Supplier Directory"
        subtitle={financial ? 'Register, account terms and compliance for your organisation' : 'Suppliers available for purchasing and deliveries'}
        actions={isAdmin && <div className="flex gap-2"><Button variant="outline" data-testid="supplier-import-button" onClick={() => setShowImport(true)}>Import workbook</Button><Button data-testid="supplier-new-button" onClick={() => setOpen(true)} className="bg-bronze hover:bg-bronze-hover text-white"><Plus size={14} className="mr-1" /> New supplier</Button></div>}
      />
      {error && <div className="mb-4"><SchemaNotice error={error} testId="suppliers-schema-notice" /></div>}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="relative max-w-sm flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="suppliers-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search name or category…" className="h-10 pl-9" />
        </div>
        <div className="text-xs text-muted-foreground" data-testid="suppliers-count">{rows.length} supplier{rows.length === 1 ? '' : 's'}</div>
      </div>
      <div className="rounded-lg border border-border bg-white overflow-x-auto">
        {isLoading ? <Loading /> : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground" data-testid="suppliers-empty">{error ? 'Nothing to show yet.' : 'No suppliers registered. Suppliers are added manually — nothing is pre-populated.'}</div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Supplier</TableHead><TableHead>Roles</TableHead><TableHead>Categories</TableHead><TableHead>Status</TableHead><TableHead>Verification</TableHead>
              {financial && <TableHead className="text-right">Terms</TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                  <TableCell>
                    <Link to={`/suppliers/${s.id}`} className="font-medium text-charcoal hover:underline" data-testid={`supplier-link-${s.id}`}>{s.display_name}</Link>
                    {s.entity?.trading_name && <div className="text-[11px] text-muted-foreground">{s.entity.legal_name}</div>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{(s.entity?.supplier_types || []).map(typeLabel).join(', ') || '—'}</TableCell>
                  <TableCell className="text-sm">{(s.categories || []).join(', ') || '—'}</TableCell>
                  <TableCell><StatusPill value={s.status} /></TableCell>
                  <TableCell><StatusPill value={s.verification_status} /></TableCell>
                  {financial && <TableCell className="text-right text-sm tabular-nums">{s.payment_terms ? `${s.payment_terms.replace('_', ' ')}${s.payment_terms_days != null ? ` · ${s.payment_terms_days}d` : ''}` : '—'}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      <SupplierImportDialog open={showImport} onOpenChange={setShowImport} organisationId={organisationId} onDone={() => mutate?.()} />
        <NewSupplierDialog open={open} onOpenChange={setOpen} organisationId={organisationId} onCreated={() => mutate()} />
    </div>
  );
}
